import { useEffect, useState } from "react";
import { getProductAnalytics } from "@/lib/api";
import { TrendUp, Eye, ShoppingCart, CurrencyDollar, ArrowsClockwise, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Analytics() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getProductAnalytics();
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totals = rows.reduce((acc, r) => ({
    views: acc.views + r.views,
    orders: acc.orders + r.orders,
    revenue: acc.revenue + r.revenue,
  }), { views: 0, orders: 0, revenue: 0 });
  const overallConv = totals.views > 0 ? (totals.orders / totals.views * 100).toFixed(2) : "0.00";

  const copyLink = (id) => {
    navigator.clipboard.writeText(`${window.location.origin}/checkout/${id}`);
    toast.success("Payment link copied");
  };

  return (
    <div className="p-8" data-testid="analytics-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-2">// data room</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">
            ANALYTICS <span className="text-arena-primary">/ FUNNEL</span>
          </h1>
          <p className="text-arena-muted mt-2 text-sm">Every payment link tracked. See which products convert, which pull traffic that never buys.</p>
        </div>
        <button
          onClick={load}
          data-testid="reload-analytics-btn"
          className="border border-arena-border px-4 py-2 text-xs tracking-widest uppercase hover:border-arena-primary hover:text-arena-primary flex items-center gap-2"
        >
          <ArrowsClockwise size={14} weight="bold" /> Refresh
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="border border-arena-border bg-arena-surface p-5" data-testid="total-views">
          <div className="flex items-start justify-between">
            <div className="overline">total views</div>
            <Eye size={16} weight="bold" className="text-arena-muted" />
          </div>
          <div className="font-heading text-4xl font-bold mt-3">{totals.views}</div>
        </div>
        <div className="border border-arena-border bg-arena-surface p-5" data-testid="total-orders">
          <div className="flex items-start justify-between">
            <div className="overline">orders</div>
            <ShoppingCart size={16} weight="bold" className="text-arena-primary" />
          </div>
          <div className="font-heading text-4xl font-bold text-arena-primary mt-3">{totals.orders}</div>
        </div>
        <div className="border border-arena-border bg-arena-surface p-5" data-testid="total-revenue">
          <div className="flex items-start justify-between">
            <div className="overline">revenue</div>
            <CurrencyDollar size={16} weight="bold" className="text-arena-success" />
          </div>
          <div className="font-heading text-4xl font-bold text-arena-success mt-3">${totals.revenue.toFixed(2)}</div>
        </div>
        <div className="border border-arena-border bg-arena-surface p-5" data-testid="total-conversion">
          <div className="flex items-start justify-between">
            <div className="overline">conversion</div>
            <TrendUp size={16} weight="bold" className="text-arena-warning" />
          </div>
          <div className="font-heading text-4xl font-bold text-arena-warning mt-3">{overallConv}%</div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-arena-border bg-arena-surface overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-arena-border text-arena-muted overline">
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-right px-4 py-3">Price</th>
              <th className="text-right px-4 py-3">Views</th>
              <th className="text-right px-4 py-3">Orders</th>
              <th className="text-right px-4 py-3">Conv %</th>
              <th className="text-right px-4 py-3">Revenue</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center py-16 text-arena-muted">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-16 text-arena-muted">No products yet. Add products first.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.product_id} className="border-b border-arena-border hover:bg-arena-surfaceHover" data-testid={`row-${r.sku}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="truncate max-w-[280px]">{r.name}</div>
                    <span className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 ${r.active ? 'bg-arena-success text-black' : 'bg-arena-secondary text-arena-muted'}`}>
                      {r.active ? 'live' : 'off'}
                    </span>
                  </div>
                  <div className="text-[10px] text-arena-muted mt-1">{r.sku}</div>
                </td>
                <td className="px-4 py-3 text-right">${r.price.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">{r.views}</td>
                <td className="px-4 py-3 text-right text-arena-primary font-bold">{r.orders}</td>
                <td className="px-4 py-3 text-right">
                  <span className={r.conversion_rate >= 2 ? "text-arena-success font-bold" : r.conversion_rate > 0 ? "text-arena-warning" : "text-arena-muted"}>
                    {r.conversion_rate.toFixed(2)}%
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-arena-success font-bold">${r.revenue.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => copyLink(r.product_id)}
                    data-testid={`copy-link-analytics-${r.sku}`}
                    className="text-arena-muted hover:text-arena-primary"
                    title="Copy payment link"
                  >
                    <Copy size={14} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-[10px] text-arena-muted">
        <span className="overline text-arena-primary">// pro tip</span>
        &nbsp;— Views &gt;100 with conversion &lt;1% = product needs better positioning. Views &lt;10 = need more ads. Both = you found gold, pour more traffic in.
      </div>
    </div>
  );
}
